import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import {
  Day,
  RangePickerComponent,
} from '@app/forms/formly/types/range-picker-dropdown/range-picker/range-picker.component';
import { NgTemplateOutlet } from '@angular/common';
import {
  formatDayToString,
  formatStringToDay,
} from '@app/forms/formly/types/range-picker-dropdown/range-picker-dropdown.helpers';
import { InputComponent, PopupsService } from '@voyagu/portal-design-system';
import { FormControl } from '@angular/forms';

export enum Range {
  From = 'from',
  To = 'to',
}

export interface RangeData {
  [Range.From]: string;
  [Range.To]: string;
}

interface AutoCompleteFormlyFieldConfig extends FormlyFieldConfig {
  formControl: FormControl<RangeData>;
}

interface Errors {
  isValidFromValidator?: { message: string };
  isValidToValidator?: { message: string };
  isFromRequired?: { message: string };
  isToRequired?: { message: string };
}

@Component({
  selector: 'range-picker-dropdown',
  templateUrl: './range-picker-dropdown.component.html',
  styleUrls: ['./range-picker-dropdown.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RangePickerComponent, NgTemplateOutlet, InputComponent],
})
export class RangePickerDropdownComponent extends FieldType<AutoCompleteFormlyFieldConfig> {
  overlayBackdropSubscription: Subscription;
  overlayRef: OverlayRef | null;
  currentSelectedValues: string[] = ['', ''];
  currentMobileSelectedValues: string[] = ['', ''];
  readonly range = Range;

  protected overlayModalHeight: string = 'auto';

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private popupsService: PopupsService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  @ViewChild('dropdownTemplate') dropdownTemplate: TemplateRef<any>;
  @ViewChild('inputElement', { read: ElementRef }) inputElement: ElementRef;
  @ViewChild('mobileTemplate') mobileTemplate: TemplateRef<unknown>;

  @HostListener('window:scroll', [])
  @HostListener('window:resize', [])
  onWindowChange() {
    if (this.overlayRef?.overlayElement) {
      this.runIsInViewportCheck(250);
    }
  }

  get fromErrorMessage(): string {
    if (!this.formControl.touched) {
      return '';
    }

    const isValidFromValidatorMessage = (this.formControl.errors as Errors)?.isValidFromValidator
      ?.message;

    if (isValidFromValidatorMessage) {
      return isValidFromValidatorMessage;
    }

    const isRequiredValidatorMessage = (this.formControl.errors as Errors)?.isFromRequired?.message;

    if (isRequiredValidatorMessage && !this.formControl.value?.from) {
      return isRequiredValidatorMessage;
    }

    return '';
  }

  get toErrorMessage(): string {
    if (!this.formControl.touched) {
      return '';
    }

    const isValidToValidator = (this.formControl.errors as Errors)?.isValidToValidator?.message;

    if (isValidToValidator) {
      return isValidToValidator;
    }

    const isRequiredValidatorMessage = (this.formControl.errors as Errors)?.isToRequired?.message;

    if (isRequiredValidatorMessage && !this.formControl.value?.to) {
      return isRequiredValidatorMessage;
    }

    return '';
  }

  openDropdown(): void {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.inputElement)
      .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    }) as OverlayRef;

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    if (this.overlayBackdropSubscription) {
      this.overlayBackdropSubscription.unsubscribe();
    }

    this.overlayBackdropSubscription = this.overlayRef.backdropClick().subscribe(() => {
      this.closeDropdown();
    });

    this.runIsInViewportCheck();
  }

  closeDropdown(): void {
    if (this.overlayRef) {
      this.formControl.markAsTouched();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  onInputClick(): void {
    if (!this.overlayRef) {
      this.openDropdown();
    }
  }

  showMobileModal(): void {
    this.currentMobileSelectedValues = this.updateCurrentSelectedValue();
    this.formControl.markAsTouched();
    this.popupsService.showModalWithFullHeightMobile(this.mobileTemplate, 0);
  }

  hideMobileModal(): void {
    this.popupsService.hideLast();
  }

  onSetDates(selectedDates: Day[]): void {
    if (!selectedDates.length) {
      return;
    }

    const end = selectedDates[1];

    this.formControl.setValue({
      [Range.From]: formatDayToString(selectedDates[0]),
      [Range.To]: end ? formatDayToString(end) : '',
    });

    this.currentSelectedValues = this.updateCurrentSelectedValue();
    this.closeDropdown();
  }

  onSelectDatesMobile(selectedDates: Day[]): void {
    if (!selectedDates.length) {
      return;
    }

    this.currentMobileSelectedValues = [formatDayToString(selectedDates[0])];

    if (selectedDates[1]) {
      this.currentMobileSelectedValues.push(formatDayToString(selectedDates[1]));
    }
  }

  onFromChanged(value: string): void {
    this.currentSelectedValues = [value, this.currentSelectedValues[1]];
  }

  onMobileToChanged(value: string): void {
    this.currentMobileSelectedValues = [this.currentMobileSelectedValues[0], value];
  }

  onMobileFromChanged(value: string): void {
    this.currentMobileSelectedValues = [value, this.currentMobileSelectedValues[1]];
  }

  onToChanged(value: string): void {
    this.currentSelectedValues = [this.currentSelectedValues[0], value];
  }

  onInputBlur(): void {
    if (this.currentSelectedValues.length > 1) {
      const start = formatStringToDay(this.currentSelectedValues[0]);
      const end = formatStringToDay(this.currentSelectedValues[1]);

      if (start && end) {
        const startDate = new Date(start.year, start.month, start.date);
        const endDate = new Date(end.year, end.month, end.date);

        if (startDate > endDate) {
          this.currentSelectedValues = [
            this.currentSelectedValues[1],
            this.currentSelectedValues[0],
          ];
        }
      }
    }

    this.updateFormControlValue();
  }

  onMobileInputBlur(): void {
    if (this.currentMobileSelectedValues.length > 1) {
      const start = formatStringToDay(this.currentMobileSelectedValues[0]);
      const end = formatStringToDay(this.currentMobileSelectedValues[1]);

      if (start && end) {
        const startDate = new Date(start.year, start.month, start.date);
        const endDate = new Date(end.year, end.month, end.date);

        if (startDate > endDate) {
          this.currentMobileSelectedValues = [
            this.currentMobileSelectedValues[1],
            this.currentMobileSelectedValues[0],
          ];
        }
      }
    }

    this.updateFormControlValue();
  }

  updateFormControlValue(): void {
    this.formControl.setValue({
      [Range.From]: this.currentSelectedValues[0] ?? '',
      [Range.To]: this.currentSelectedValues[1] ?? '',
    });
  }

  updateCurrentSelectedValue(): string[] {
    const currentSelectedValue = [];
    const value = this.formControl.value;
    const start = value?.from;
    const end = value?.to;

    if (start) {
      currentSelectedValue.push(start);
    }

    if (end) {
      currentSelectedValue.push(end);
    }

    return currentSelectedValue;
  }

  protected runIsInViewportCheck(delay = 0): void {
    setTimeout(() => {
      const rangePicker = this.overlayRef?.overlayElement.querySelector(
        '#range-picker',
      ) as HTMLElement;
      const child = rangePicker.children[0] as HTMLElement;
      const overlayBottomOffset = this.getOverlayViewportBottomOffset(rangePicker);
      this.overlayModalHeight = overlayBottomOffset
        ? `${child.clientHeight - overlayBottomOffset}px`
        : 'auto';
      this.cdr.detectChanges();
    }, delay);
  }

  private getOverlayViewportBottomOffset(element: HTMLElement): number {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return Math.max(0, rect.top + element.children[0].clientHeight - viewportHeight);
  }
}
